import { componentTypography } from '@/theme/componentTypography';
import { s, typographyTokens } from '@/theme/tokens';
import { filterTokens } from '@/ui/composites/filters/filterTokens';
import { overlayScrollbarSx } from '@/ui/primitives/overlayScrollbarSx';
import type { SxProps, Theme } from '@mui/material/styles';

const resolveTypography = (
  theme: Theme,
  role: keyof typeof typographyTokens,
): { fontSize: string | number; fontWeight: number; lineHeight: string | number } => {
  const token = typographyTokens[role];
  const typography = theme.typography[token.variant];
  const fallback = theme.typography.body1;
  return {
    fontSize: typography.fontSize ?? fallback.fontSize ?? theme.typography.fontSize,
    fontWeight: token.fontWeight,
    lineHeight: typography.lineHeight ?? fallback.lineHeight ?? 1.5,
  };
};

type SxObject = Record<string, unknown>;

const isSxObject = (value: unknown): value is SxObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const resolveSxObject = (sx: SxProps<Theme>, theme: Theme): SxObject => {
  if (!sx) return {};
  const resolved: unknown = typeof sx === 'function' ? sx(theme) : sx;
  if (Array.isArray(resolved)) {
    return resolved.reduce<SxObject>((acc, item) => {
      if (isSxObject(item)) {
        return { ...acc, ...item };
      }
      return acc;
    }, {});
  }
  if (isSxObject(resolved)) {
    return resolved;
  }
  return {};
};

export const filterTypographyRole: keyof typeof typographyTokens = 'caption';

export const mergeSx =
  (base: SxObject, extra: SxProps<Theme>): SxProps<Theme> =>
  (theme) => ({ ...base, ...resolveSxObject(extra, theme) });

export const mergeFilterSlotSx =
  (base: SxProps<Theme>, extra: SxObject): SxProps<Theme> =>
  (theme) => ({ ...resolveSxObject(base, theme), ...extra });

export const filterInputLabelSx: SxProps<Theme> = (theme) => resolveTypography(theme, 'caption');

export const filterInputSx: SxProps<Theme> = (theme) => ({
  '& .MuiInputBase-root': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    height: filterTokens.controlMinHeight,
    alignItems: 'center',
    color: theme.palette.common.white,
  },
  '& .MuiOutlinedInput-root': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    height: filterTokens.controlMinHeight,
    color: theme.palette.common.white,
  },
  '& .MuiPickersInputBase-root': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    height: filterTokens.controlMinHeight,
    alignItems: 'center',
    color: theme.palette.common.white,
  },
  '& .MuiInputBase-input': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    display: 'flex',
    alignItems: 'center',
    paddingTop: s('sm'),
    paddingBottom: s('sm'),
    color: theme.palette.common.white,
  },
  '& .MuiOutlinedInput-input': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    display: 'flex',
    alignItems: 'center',
    paddingTop: s('sm'),
    paddingBottom: s('sm'),
    color: theme.palette.common.white,
  },
  '& .MuiPickersInputBase-input': {
    ...resolveTypography(theme, filterTypographyRole),
    minHeight: filterTokens.controlMinHeight,
    display: 'flex',
    alignItems: 'center',
    paddingTop: s('sm'),
    paddingBottom: s('sm'),
    paddingLeft: filterTokens.dateInputPaddingLeft,
    color: theme.palette.common.white,
  },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & .MuiPickersInputBase-input::placeholder':
    {
      color: theme.palette.common.white,
      opacity: 1,
    },
  '& .MuiInputLabel-root': { ...resolveTypography(theme, 'caption') },
});

export const filterSelectSx: SxProps<Theme> = (theme) => {
  const typography = resolveTypography(theme, filterTypographyRole);
  return {
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    lineHeight: typography.lineHeight,
    '& .MuiInputBase-root': {
      ...typography,
      minHeight: filterTokens.controlMinHeight,
      height: filterTokens.controlMinHeight,
      alignItems: 'center',
      boxSizing: 'border-box',
      color: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-root': {
      ...typography,
      minHeight: filterTokens.controlMinHeight,
      height: filterTokens.controlMinHeight,
      alignItems: 'center',
      boxSizing: 'border-box',
      color: theme.palette.common.white,
    },
    '& .MuiSelect-select': {
      ...typography,
      fontStyle: 'normal',
      minHeight: filterTokens.controlMinHeight,
      height: filterTokens.controlMinHeight,
      display: 'flex',
      alignItems: 'center',
      fontSize: typography.fontSize,
      lineHeight: typography.lineHeight,
      paddingTop: s('none'),
      paddingBottom: s('none'),
      paddingLeft: filterTokens.selectPaddingLeft,
      paddingRight: s('lg'),
      boxSizing: 'border-box',
      color: theme.palette.common.white,
    },
    '& .MuiSelect-select.MuiInputBase-input': { ...typography, fontStyle: 'normal' },
    '& .MuiSelect-select, & .MuiSelect-select.MuiInputBase-input, & .MuiSelect-select span': {
      fontSize: typography.fontSize,
      lineHeight: typography.lineHeight,
      fontWeight: typography.fontWeight,
      color: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-root .MuiSelect-select': {
      height: filterTokens.controlMinHeight,
      minHeight: filterTokens.controlMinHeight,
    },
    '& .MuiSelect-select *': { ...typography, fontStyle: 'normal' },
    '& .MuiInputLabel-root': { ...resolveTypography(theme, 'caption') },
    '& .MuiSelect-icon': {
      fontSize: componentTypography.filters.iconFontSize,
      color: theme.palette.common.white,
    },
  };
};

export const filterMenuItemSx: SxProps<Theme> = (theme) => ({
  ...resolveTypography(theme, filterTypographyRole),
});

export const filterMenuItemTextSx: SxProps<Theme> = (theme) =>
  resolveTypography(theme, filterTypographyRole);

export const filterIconSx: SxProps<Theme> = { fontSize: componentTypography.filters.iconFontSize };

export const buildFilterAutocompleteListboxSx = (
  optionRowMinHeight: string,
  maxVisibleOptions = 10,
): SxProps<Theme> => [
  {
    maxHeight: `calc(${String(maxVisibleOptions)} * ${optionRowMinHeight})`,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  overlayScrollbarSx,
];
